import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AudioPlayer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faForward,
    faBackward,
    faVolumeUp
} from '@fortawesome/free-solid-svg-icons';

interface SoundCloudHybridPlayerProps {
    playlistUrl: string;
}

interface TrackInfo {
    id: string;
    title: string;
    artist: string;
    artwork?: string;
    duration: number;
}

/**
 * Custom UI Audio Player that uses SoundCloud Widget API for playback
 * Maintains your beautiful custom design while using SoundCloud for streaming
 */
const SoundCloudHybridPlayer: React.FC<SoundCloudHybridPlayerProps> = ({ playlistUrl }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTrack, setCurrentTrack] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(100);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [tracks, setTracks] = useState<TrackInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const widgetRef = useRef<any>(null);
    const [iframeElement, setIframeElement] = useState<HTMLIFrameElement | null>(null);

    // Callback ref to track when iframe is mounted
    const handleIframeRef = (element: HTMLIFrameElement | null) => {
        setIframeElement(element);
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!iframeElement) return;

        const initWidget = async () => {
            try {
                // Load SoundCloud Widget API
                await loadWidgetAPI();

                // Initialize widget
                widgetRef.current = (window as any).SC.Widget(iframeElement);

                // Add timeout to catch if READY event never fires
                const readyTimeout = setTimeout(() => {
                    setError('Playlist load timeout. Check if playlist is public and URL is correct.');
                    setLoading(false);
                }, 10000);

                // Set up event listeners
                widgetRef.current.bind((window as any).SC.Widget.Events.READY, () => {
                    clearTimeout(readyTimeout);

                    // Get playlist info
                    widgetRef.current.getSounds((sounds: any[]) => {
                        if (sounds && sounds.length > 0) {
                            const trackList: TrackInfo[] = sounds.map((sound: any) => ({
                                id: sound.id.toString(),
                                title: sound.title,
                                artist: sound.user?.username || 'Unknown Artist',
                                artwork: sound.artwork_url,
                                duration: sound.duration / 1000,
                            }));

                            setTracks(trackList);
                            setLoading(false);

                            // Get initial duration
                            widgetRef.current.getDuration((d: number) => {
                                setDuration(d / 1000);
                            });
                        } else {
                            setError('No tracks found in playlist. Make sure playlist is public.');
                            setLoading(false);
                        }
                    });

                    // Set initial volume
                    widgetRef.current.setVolume(volume);
                });

                widgetRef.current.bind((window as any).SC.Widget.Events.PLAY, () => {
                    setIsPlaying(true);
                });

                widgetRef.current.bind((window as any).SC.Widget.Events.PAUSE, () => {
                    setIsPlaying(false);
                });

                widgetRef.current.bind((window as any).SC.Widget.Events.FINISH, () => {
                    nextTrack();
                });

                widgetRef.current.bind((window as any).SC.Widget.Events.PLAY_PROGRESS, (data: any) => {
                    setCurrentTime(data.currentPosition / 1000);
                });

                widgetRef.current.bind((window as any).SC.Widget.Events.PLAY, () => {
                    widgetRef.current.getDuration((d: number) => {
                        setDuration(d / 1000);
                    });
                });

            } catch (err) {
                setError('Failed to load SoundCloud player');
                setLoading(false);
            }
        };

        initWidget();
    }, [iframeElement, playlistUrl, volume]);

    const togglePlay = () => {
        if (widgetRef.current) {
            widgetRef.current.toggle();
        }
    };

    const nextTrack = () => {
        if (widgetRef.current) {
            widgetRef.current.next();
            widgetRef.current.getCurrentSoundIndex((index: number) => {
                setCurrentTrack(index);
            });
        }
    };

    const prevTrack = () => {
        if (widgetRef.current) {
            widgetRef.current.prev();
            widgetRef.current.getCurrentSoundIndex((index: number) => {
                setCurrentTrack(index);
            });
        }
    };

    const selectTrack = (index: number) => {
        if (widgetRef.current) {
            widgetRef.current.skip(index);
            setCurrentTrack(index);
        }
    };

    const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = Number(e.target.value);
        if (widgetRef.current) {
            widgetRef.current.seekTo(time * 1000); // Convert to milliseconds
            setCurrentTime(time);
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (widgetRef.current) {
            widgetRef.current.setVolume(newVolume);
        }
    };

    const toggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(prev => !prev);
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const currentTrackData = tracks[currentTrack];

    return (
        <>
            {/* Hidden SoundCloud iframe for playback - MUST always render for initialization */}
            <iframe
                title="SoundCloud Player"
                ref={handleIframeRef}
                width="0"
                height="0"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(playlistUrl)}&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&visual=false`}
                style={{ display: 'none' }}
            />

            {loading ? (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    zIndex: 1000
                }}>
                    Loading playlist...
                </div>
            ) : error ? (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    background: 'rgba(255, 0, 0, 0.8)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    zIndex: 1000
                }}>
                    Error: {error}
                </div>
            ) : tracks.length === 0 ? null : (
                /* Your Custom Player UI */
                <motion.div
                className={`audio-player ${isExpanded ? 'expanded' : 'minimized'}`}
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Minimized view */}
                <div className="player-minimized" onClick={toggleExpand}>
                    <div className="player-bottom">
                        <div className="track-info">
                            <div className="track-details-mini">
                                <div className="track-title-mini">{currentTrackData?.title}</div>
                                <div className="track-artist-mini">{currentTrackData?.artist}</div>
                            </div>
                        </div>

                        <div className="player-controls-mini">
                            <button onClick={(e) => { e.stopPropagation(); prevTrack(); }} className="control-btn">
                                <FontAwesomeIcon icon={faBackward} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); togglePlay(); }} className="play-btn">
                                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); nextTrack(); }} className="control-btn">
                                <FontAwesomeIcon icon={faForward} />
                            </button>
                        </div>

                        {/* Progress bar - only shown on desktop */}
                        {!isMobile && (
                            <div className="progress-section-mini">
                                <input
                                    type="range"
                                    min="0"
                                    max={duration || 0}
                                    value={currentTime}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleProgressChange(e);
                                    }}
                                    className="progress-slider-mini"
                                />
                                <div className="time-display-mini">
                                    <span>{formatTime(currentTime)}</span>
                                    <span>{formatTime(duration)}</span>
                                </div>
                            </div>
                        )}

                        {/* Volume control - only shown on desktop */}
                        {!isMobile && (
                            <div className="volume-control-mini" onClick={e => e.stopPropagation()}>
                                <FontAwesomeIcon icon={faVolumeUp} className="volume-icon" />
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="5"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="volume-slider"
                                    aria-label="Volume"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Expanded view */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            className="player-expanded"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Mobile controls section */}
                            {isMobile && (
                                <div className="mobile-controls-section">
                                    {/* Progress Section */}
                                    <div className="progress-section-mobile">
                                        <input
                                            type="range"
                                            min="0"
                                            max={duration || 0}
                                            value={currentTime}
                                            onChange={handleProgressChange}
                                            className="progress-slider-mobile"
                                        />
                                        <div className="time-display-mobile">
                                            <span>{formatTime(currentTime)}</span>
                                            <span>{formatTime(duration)}</span>
                                        </div>
                                    </div>

                                    {/* Volume Control */}
                                    <div className="volume-control-mobile">
                                        <FontAwesomeIcon icon={faVolumeUp} className="volume-icon" />
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="5"
                                            value={volume}
                                            onChange={handleVolumeChange}
                                            className="volume-slider-mobile"
                                            aria-label="Volume"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="playlist">
                                <h4>Playlist</h4>
                                <div className="track-list">
                                    {tracks.map((track, index) => (
                                        <div
                                            key={track.id}
                                            className={`track-item ${index === currentTrack ? 'active' : ''}`}
                                            onClick={() => selectTrack(index)}
                                        >
                                            <div className="track-number">{index + 1}</div>
                                            <div className="track-info-item">
                                                <div className="track-title">{track.title}</div>
                                                <div className="track-artist">{track.artist}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
            )}
        </>
    );
};

// Helper function to load the SoundCloud Widget API script
function loadWidgetAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
        // Check if already loaded
        if ((window as any).SC?.Widget) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://w.soundcloud.com/player/api.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load SoundCloud Widget API'));
        document.head.appendChild(script);
    });
}

export default SoundCloudHybridPlayer;
